import { useTranslation } from "react-i18next";
import { Box, Chip, Divider, Skeleton, Typography } from "@mui/material";

import { Availability } from "../../types/service.type";
import { useMemo } from "react";

const ServiceAvailability = ({ availability, isLoading }: { availability?: Availability; isLoading: boolean }) => {
  const { t } = useTranslation("goService");

  const alwaysAvailableList = useMemo(() => {
    const list: string[] = [];
    if (availability?.alwaysAvailable) {
      list.push(t("servicePage.alwaysAvailable"));
    } else if (availability?.availableAllDay) {
      list.push(t("servicePage.availableAllDay"));
    }

    return list;
  }, [availability?.availableAllDay, availability?.alwaysAvailable, t]);

  if (isLoading) {
    return <ServiceAvailabilityLoading />;
  }

  const hideAvailability = !availability?.months && !availability?.days && !availability?.from && !availability?.to;

  if (hideAvailability) return null;

  return (
    <Box sx={styles.container}>
      <Box sx={styles.content}>
        <Typography sx={styles.title}>{t("servicePage.availability")}</Typography>
        <Typography sx={styles.description}>{t("servicePage.preferredServiceDateTiming")}</Typography>
      </Box>
      {alwaysAvailableList.length > 0 && <AvailabilitySection chips={alwaysAvailableList} />}
      {Boolean(availability?.months) ||
        Boolean(availability?.days) ||
        Boolean(availability?.from) ||
        (Boolean(availability?.to) && <Divider sx={styles.divider} />)}
      {availability?.months && availability?.months.length > 0 && (
        <AvailabilitySection title={t("servicePage.mostAvailableMonths")} chips={availability.months!} isMonth={true} />
      )}
      {availability?.days && availability?.days.length > 0 && (
        <AvailabilitySection title={t("servicePage.mostAvailableDays")} chips={availability.days!} isDay={true} />
      )}
      {Boolean(availability?.from) && Boolean(availability?.to) && (
        <AvailabilitySection
          title={t("servicePage.mostAvailableTime")}
          chips={[availability.from!, availability.to!]}
        />
      )}
    </Box>
  );
};

export default ServiceAvailability;

const AvailabilitySection = ({
  title,
  chips,
  isMonth,
  isDay,
}: {
  title?: string;
  chips: string[];
  isMonth?: boolean;
  isDay?: boolean;
}) => {
  const { t } = useTranslation("common");
  const getChipLabel = (label: string) => (isMonth ? t(`month.${label}`) : isDay ? t(`day.${label}`) : label);

  return (
    <Box sx={styles.sectionContainer}>
      {title && <Typography sx={styles.title}>{title}</Typography>}
      <Box sx={styles.chips}>
        {chips.map((value) => (
          <Chip key={value} label={getChipLabel(value)} sx={styles.chip} />
        ))}
      </Box>
    </Box>
  );
};

const ServiceAvailabilityLoading = () => {
  const { t } = useTranslation("goService");

  return (
    <Box sx={styles.container}>
      <Box sx={styles.content}>
        <Typography sx={styles.title}>{t("servicePage.availability")}</Typography>
        <Typography sx={styles.description}>{t("servicePage.preferredServiceDateTiming")}</Typography>
      </Box>
      <Divider sx={styles.divider} />
      <Box sx={styles.loadingSection}>
        <Skeleton variant="text" width={160} height={24} />
        <Box sx={styles.loadingChipsContainer}>
          <Skeleton variant="rounded" width={80} height={32} />
          <Skeleton variant="rounded" width={90} height={32} />
          <Skeleton variant="rounded" width={70} height={32} />
        </Box>
      </Box>
      <Box sx={styles.loadingSection}>
        <Skeleton variant="text" width={140} height={24} />
        <Box sx={styles.loadingChipsContainer}>
          <Skeleton variant="rounded" width={100} height={32} />
          <Skeleton variant="rounded" width={85} height={32} />
          <Skeleton variant="rounded" width={95} height={32} />
        </Box>
      </Box>
      <Box sx={styles.loadingSection}>
        <Skeleton variant="text" width={150} height={24} />
        <Box sx={styles.loadingChipsContainer}>
          <Skeleton variant="rounded" width={100} height={32} />
          <Skeleton variant="rounded" width={100} height={32} />
        </Box>
      </Box>
    </Box>
  );
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    padding: "24px",
    borderRadius: "8px",
    border: "1px solid #EAECF0",
    background: "var(--White, #FFF)",
  },
  sectionContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  divider: {
    mx: "-24px",
  },
  content: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  title: {
    color: "var(--Gray-900, #1D2026)",
    fontFamily: "Montserrat",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "normal",
  },
  description: {
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "150%",
  },
  chip: {
    color: "var(--Green---100, #008B07)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
    padding: "4px 12px",
    borderRadius: "16px",
    background: "var(--Green---25, #E7EFED)",
    "& .MuiChip-label": {
      padding: "0",
    },
  },
  chips: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
  },
  loadingSection: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  loadingChipsContainer: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
  },
} as const;
